<script lang="ts" setup>
import { IconName } from "../Icon/utils";
import Icon from "../Icon/Icon.vue";
import Spinner from "./Spinner.vue";

const props = withDefaults(
  defineProps<{
    type?: "button" | "submit" | "reset";
    label?: string | null;
    icon?: IconName | null;
    iconRight?: IconName | null;
    iconOnly?: boolean;
    isDisabled?: boolean;
    isLoading?: boolean;
    isRounded?: boolean;
    isFullWidth?: boolean;
    // Renders a real anchor instead of a button, so the target can be
    // middle-clicked or cmd-clicked into a new tab.
    href?: string | null;
    target?: string | null;
  }>(),
  {
    type: "button",
    label: null,
    icon: null,
    iconRight: null,
    iconOnly: false,
    isDisabled: false,
    isLoading: false,
    isRounded: false,
    isFullWidth: false,
    href: null,
    target: null,
  }
);

const emit = defineEmits<{
  (event: "click", e: MouseEvent): void;
}>();

const handleClick = (e: MouseEvent) => {
  if (props.isDisabled || props.isLoading) {
    if (props.href) e.preventDefault();
    return;
  }
  // A modifier or middle click is the browser's to handle — leaving it alone is
  // what lets the link open in a new tab.
  if (
    props.href &&
    (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)
  ) {
    return;
  }
  emit("click", e);
};

const buttonClasses = `
btn inline-flex cursor-pointer items-center justify-center bg-button-bg dark:bg-button-bg align-middle text-white text-lg dark:text-white hover:bg-button-bg dark:hover:bg-button-bg
  ${props.isFullWidth ? "w-full" : ""}
  ${props.isRounded ? "rounded-full" : "rounded-md"}
  ${props.iconOnly ? "px-3" : "gap-2 px-7 py-3"}
  `;
</script>

<template>
  <component
    :is="href ? 'a' : 'button'"
    :type="href ? undefined : type"
    :href="href ?? undefined"
    :target="href ? (target ?? '_blank') : undefined"
    :rel="href && (target ?? '_blank') === '_blank' ? 'noopener' : undefined"
    :disabled="href ? undefined : isDisabled || isLoading"
    :aria-disabled="isDisabled || isLoading"
    :class="buttonClasses + ' cursor-pointer no-underline'"
    @click="handleClick"
  >
    <icon :name="icon" :class="['h-4 w-4']" v-if="icon" />
    <span :class="[]" v-if="!iconOnly">
      {{ label }}
    </span>
    <icon :name="iconRight" :class="['h-4 w-4']" v-if="iconRight" />
    <spinner :size="6" color="white" v-if="isLoading" />
  </component>
</template>
